import { Column, ColumnConfig, DateHelper, NumberColumn } from '@bryntum/grid';
import { DateTimeHelper } from '../../DateTime/DateTimeHelper';

const getGenericCheckIconForBooleanColumnConfig = () => {
    return 'bi bi-check-circle-fill fs-5 green-extreme';
}

const getGenericXmarkIconForBooleanColumnConfig = () => {
    return 'bi bi-x-circle-fill fs-5 danger-extreme';
}

const getBooleanColumnGenericConfig = (trueIconClasses: string | undefined = undefined, falseIconClasses: string | undefined = undefined) => {
    const templateConfig = getBooleanColumnGenericTemplateConfig(trueIconClasses, falseIconClasses);
    return {
        ...templateConfig,
        renderer: ({ value, isExport }: { value: boolean, isExport: boolean }) => {
            if (isExport) {
                if (value === null || value === undefined) {
                    return '';
                }
                return value ? 'Yes' : 'No';
            }
            return templateConfig.template({ value });
        },
        ...getBooleanColumnFilterableWithYesNoCombo(),
    }
}

const getBooleanColumnGenericTemplateConfig = (trueIconClasses: string | undefined = undefined, falseIconClasses: string | undefined = undefined) => {
    return {
        template: ({ value }: { value: boolean }) => {
            // Default
            if (!trueIconClasses && !falseIconClasses) {
                const iconClassName = value ? getGenericCheckIconForBooleanColumnConfig() : getGenericXmarkIconForBooleanColumnConfig();

                return {
                    tag: 'i',
                    className: iconClassName
                };
            }

            // Override true or false only or both
            if (value && trueIconClasses) {
                return {
                    tag: 'i',
                    className: trueIconClasses,
                };
            } else if (!value && falseIconClasses) {
                return {
                    tag: 'i',
                    className: falseIconClasses,
                };
            }

            return "";
        }
    }
}

const getBooleanColumnFilterableWithYesNoCombo = () => {
    return {
        filterable: {
            filterField: {
                type: "combo",
                multiSelect: false,
                valueField: 'value',
                displayField: 'text',
                items: [
                    { "text": "YES", "value": true },
                    { "text": "NO", "value": false },
                ]
            }
        }
    }
}

// Bryntum supports numeric export values, but its renderer typings only describe display values.
const getNumberColumnGenericConfig = () => ({
    exportedType: 'number',
    excelFormat: (value: number) => Number.isInteger(value) ? '0' : '0.###############',
    renderer: (({ value, isExport, column }: { value: number | null | undefined, isExport: boolean, column: Column }) => {
        if (value === null || value === undefined) {
            return '';
        }
        if (isExport) {
            return Number.isFinite(value) ? value : null;
        }
        if (column instanceof NumberColumn) {
            return column.defaultRenderer({ value });
        }
        return value;
    }) as ColumnConfig['renderer']
});

const getTimeColumnGenericConfig = (format = 'HH:mm', excelFormat = 'hh:mm') => ({
    exportedType: 'number',
    excelFormat,
    renderer: (({ value, isExport }: { value: string | Date | null, isExport: boolean }) => {
        const time = DateTimeHelper.parseBryntumTime(value);
        if (!time) {
            return '';
        }
        if (isExport) {
            return (time.getHours() * 3600 + time.getMinutes() * 60 + time.getSeconds()) / 86400;
        }
        return DateHelper.format(time, format);
    }) as ColumnConfig['renderer']
});

export { getBooleanColumnGenericConfig, getNumberColumnGenericConfig, getTimeColumnGenericConfig };
